import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError, money } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { IdentificationCard, Plus, Trash, CheckCircle, WarningCircle, FolderOpen, FileText, ShieldCheck } from "@phosphor-icons/react";
import { CameraCapture } from "../components/CameraCapture";

const DOC_TYPES = [
  { key: "identificacion", label: "Identificación oficial" },
  { key: "comprobante_domicilio", label: "Comprobante de domicilio" },
];

export default function Employees() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ contractor_id: "", name: "", position: "", nss: "", monthly_salary: "" });
  const [docEmp, setDocEmp] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const load = () => api.get("/employees").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/contractors").then((r) => setContractors(r.data)); }, []);

  const cmap = Object.fromEntries(contractors.map((c) => [c.id, c]));

  const save = async (e) => {
    e.preventDefault();
    if (!form.contractor_id) return toast.error("Selecciona un contratista");
    if (!photoFile) return toast.error("Toma la fotografía del empleado para poder registrarlo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("contractor_id", form.contractor_id);
      fd.append("name", form.name);
      fd.append("position", form.position || "");
      fd.append("nss", form.nss || "");
      fd.append("monthly_salary", parseFloat(form.monthly_salary || 0));
      fd.append("photo", photoFile);
      await api.post("/employees", fd);
      toast.success("Empleado agregado"); setOpen(false);
      setForm({ contractor_id: "", name: "", position: "", nss: "", monthly_salary: "" }); setPhotoFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar empleado?")) return; await api.delete(`/employees/${id}`); load(); };

  const uploadDoc = async (docType, file) => {
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    try {
      const { data } = await api.post(`/employees/${docEmp.id}/document`, fd);
      setDocEmp(data); toast.success("Documento cargado"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const authorized = items.filter((e) => e.authorized).length;

  return (
    <div>
      <PageHeader overline="Personal en obra" title="Empleados"
        action={isManager && <PrimaryButton onClick={() => setOpen(true)} data-testid="add-employee-btn"><Plus size={18} weight="bold" /> Nuevo</PrimaryButton>} />

      {items.length > 0 && (
        <Card className="p-4 mb-6 flex items-center gap-3 border-l-4 border-l-primary">
          <ShieldCheck size={22} weight="bold" className="text-primary" />
          <p className="text-sm text-muted-foreground">
            <b className="text-foreground">{authorized}</b> de <b className="text-foreground">{items.length}</b> empleados autorizados para acceso.
            Un empleado requiere <b className="text-foreground">NSS, identificación y comprobante de domicilio</b> para ser autorizado en el checador.
          </p>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={IdentificationCard} title="Sin empleados" subtitle="Registra al personal de cada contratista y su papelería para autorizar accesos."
          action={isManager && <PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Agregar empleado</PrimaryButton>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Nombre", "Puesto", "Empresa", "NSS", "Salario", "Autorización", ""].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-4 py-3 font-semibold"><div className="flex items-center gap-2">{e.photo_path && <img src={fileUrl(e.photo_path)} alt="" className="w-8 h-8 rounded-sm object-cover border border-border" />}{e.name}</div></td>
                  <td className="px-4 py-3 text-muted-foreground">{e.position || "—"}</td>
                  <td className="px-4 py-3">{e.company || cmap[e.contractor_id]?.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.nss || "—"}</td>
                  <td className="px-4 py-3 font-medium">{money(e.monthly_salary)}</td>
                  <td className="px-4 py-3">
                    {e.authorized
                      ? <span className="inline-flex items-center gap-1 text-success font-semibold"><CheckCircle size={16} weight="fill" /> Autorizado</span>
                      : <span className="inline-flex items-center gap-1 text-danger font-semibold"><WarningCircle size={16} weight="fill" /> Falta papelería</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isManager && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setDocEmp(e)} data-testid={`docs-btn-${e.id}`} title="Documentos" className="p-1.5 text-muted-foreground hover:text-primary"><FolderOpen size={18} /></button>
                        {canDelete && <button onClick={() => remove(e.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* New employee */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo empleado">
        <form onSubmit={save} className="space-y-4">
          <Field label="Contratista *">
            <select data-testid="employee-contractor" required className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
              <option value="">Selecciona…</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Nombre *"><input data-testid="employee-name" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Puesto"><input className={inputClass} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></Field>
            <Field label="NSS *"><input data-testid="employee-nss" className={inputClass} value={form.nss} onChange={(e) => setForm({ ...form, nss: e.target.value })} /></Field>
          </div>
          <Field label="Salario mensual (MXN)"><input data-testid="employee-salary" type="number" step="0.01" className={inputClass} value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} /></Field>
          <Field label="Fotografía del empleado * (se usará para verificar su identidad con IA en el control de acceso)"><CameraCapture onCapture={setPhotoFile} /></Field>
          <p className="text-xs text-muted-foreground">La foto es obligatoria. Después de guardar, sube la papelería (identificación y comprobante de domicilio) desde el botón de documentos para autorizar su acceso.</p>
          <PrimaryButton type="submit" disabled={busy || !photoFile} data-testid="save-employee-btn">{busy ? "Guardando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>

      {/* Documents */}
      <Modal open={!!docEmp} onClose={() => setDocEmp(null)} title={`Papelería · ${docEmp?.name || ""}`}>
        {docEmp && (
          <div className="space-y-4">
            <div className={`rounded-sm p-3 text-sm ${docEmp.authorized ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
              {docEmp.authorized
                ? <span className="inline-flex items-center gap-1 font-semibold"><CheckCircle size={16} weight="fill" /> Empleado autorizado para acceso</span>
                : <span className="inline-flex items-center gap-1 font-semibold"><WarningCircle size={16} weight="fill" /> Falta: {(docEmp.missing_docs || []).join(", ")}</span>}
            </div>

            <div className={`rounded-sm p-3 text-sm border ${docEmp.nss ? "border-border" : "border-danger bg-red-50/40"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">Número de Seguro Social (NSS)</span>
                {docEmp.nss ? <span className="text-success inline-flex items-center gap-1"><CheckCircle size={15} weight="fill" /> {docEmp.nss}</span> : <span className="text-danger text-xs">Captúralo al editar</span>}
              </div>
            </div>

            {DOC_TYPES.map((d) => {
              const has = (docEmp.documents || []).find((x) => x.doc_type === d.key);
              return (
                <div key={d.key} className="rounded-sm border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{d.label}</span>
                    {has
                      ? <a href={fileUrl(has.path)} target="_blank" rel="noreferrer" className="text-primary text-sm inline-flex items-center gap-1 hover:underline"><FileText size={15} weight="fill" /> Ver</a>
                      : <span className="text-danger text-xs font-semibold">Pendiente</span>}
                  </div>
                  <label className="mt-2 inline-flex items-center gap-2 text-sm text-primary font-medium cursor-pointer hover:underline" data-testid={`upload-${d.key}`}>
                    <Plus size={15} weight="bold" /> {has ? "Reemplazar" : "Subir"} documento
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(d.key, e.target.files[0])} />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}
